import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Select, Space, Spin, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { getApi, postApi } from 'redux/apis';
import { toastText } from 'utils/utils';
import { showNotification } from 'utils/notification';
import type { WizardState, WizardAction } from '../wizardReducer';
import { aggregateByEmployee, confidenceByEmployee, findNameColumns, isTotalRow } from '../parsers';
import type { NameColumnConfig } from '../parsers/types';
import {
	buildAggregatedRows,
	computeEmployeeRows,
	type RosterEmployee,
} from './step3FuzzyMatch';
import styles from './Step3MapEmployees.module.scss';

interface Step3MapEmployeesProps {
	state: WizardState;
	dispatch: React.Dispatch<WizardAction>;
	companyId: string;
	payPeriodId: string | null;
	onBack: () => void;
	onCancel: () => void;
}

const EMPLOYEES_URL = '/employees/list';
const ALIASES_URL = '/employee-cost/import/name-aliases';
const PRIOR_PERIOD_VALUES_URL = '/employee-cost/import/pdf/prior-period-values';
// Sentinel option value for the pinned "Add as new employee" override.
const ADD_AS_NEW_VALUE = '__add_as_new__';
// ENG-414: the pinned "Skip" entry needs no sentinel value — unlike add-as-new it
// never routes through handleOverrideChange, so there is nothing to disambiguate
// from an employee id. (Note: row-level Skip is unrelated to the Step 2 'skip'
// column category, which means "ignore this column".)
//
// Advisory shown once per session on the first Skip. The approved copy leads with
// "Heads up:", which maps onto the antd notification's title/description split.
const SKIP_NOTICE_TITLE = 'Heads up';
const SKIP_NOTICE_DESCRIPTION =
	"If you're skipping more than one row, it may be faster to remove those rows from your source file before importing. Skipped rows won't be imported, but they'll still appear here each time you import this file.";
// antd durations are SECONDS, and 0 means "never auto-close" (Infinity is not a
// valid antd duration). Keeps the fires-once advisory on screen until the user
// dismisses it with the X.
const SKIP_NOTICE_DURATION = 0;
// The Modal body (SmartImportWizard.tsx) caps itself at 85vh with its own
// overflow:auto as a last-resort fallback. A fixed pixel scroll.y here (the
// old value was 420) doesn't know about that budget, so on a shorter
// viewport the table's own internal scrollbar AND the modal body's fallback
// scrollbar can both end up visible at once — two scrollbars for what reads
// as one list. Sizing this off 85vh keeps the table itself as the only
// scroll surface in the normal case; `max(...)` keeps a sane floor so the
// table never collapses to near-zero height on a very short viewport (the
// modal's own fallback scroll still catches that rare case).
const TABLE_SCROLL_Y = 'max(240px, calc(85vh - 300px))';

type EmployeeRow = WizardState['employeeRows'][number];

// ENG-421: within a set of mapped name columns, identify the one holding the
// surname so `multi` layouts can assemble the "Last, First" format the backend
// requires. An explicit "Last Name" header wins; otherwise the primary name column
// (ADP-style exports put the surname in "Employee Name" / "Full Name" and break out
// first/middle into their own columns). Returns undefined when nothing identifies a
// surname column — the caller then falls back to the ambiguous space-join.
export function detectLastNameIndex(
	indices: number[],
	headers: string[]
): number | undefined {
	const explicit = indices.find((i) => /last.?name/i.test(headers[i] ?? ''));
	if (explicit !== undefined) return explicit;
	return indices.find(
		(i) =>
			/^(employee|worker|associate|staff)$/i.test((headers[i] ?? '').trim()) ||
			/employee name|emp name|full name/i.test(headers[i] ?? '')
	);
}

// Build the name-column layout the aggregator needs from Step 2's employee_name
// mappings. ENG-409: every column mapped to Employee Name is combined, in file
// column order (by colIndex). (The old Combine/Use-as-is toggle is gone.)
//
// The detected split-name pair keeps the "Last, First" comma format (the common
// case, consistent with pre-ENG-409 Step 3 display and alias keys): if the two
// mapped columns are exactly the Last/First pair findNameColumns re-derives from the
// header row, use the `combined` config. Any other shape — a single column, or 3+ /
// arbitrary columns — uses the generic `multi`. ENG-421: for `multi`, tag the
// surname column (lastNameIndex) by header heuristic so getEmployeeName assembles
// "Last, First Middle"; if none is identifiable it falls back to the space-join.
export function buildNameColConfig(
	state: WizardState
): NonNullable<NameColumnConfig> | null {
	const indices = state.columnMappings
		.filter((m) => m.category === 'employee_name')
		.map((m) => m.colIndex)
		.sort((a, b) => a - b);
	if (indices.length === 0) return null;

	const headers = (state.rawMatrix[state.headerRowIndex] ?? []).map(String);

	if (indices.length === 2) {
		const detected = findNameColumns(headers);
		if (
			detected?.type === 'combined' &&
			indices.includes(detected.lastNameIndex) &&
			indices.includes(detected.firstNameIndex)
		) {
			return detected;
		}
	}

	const lastNameIndex = detectLastNameIndex(indices, headers);
	return lastNameIndex !== undefined
		? { type: 'multi', indices, lastNameIndex }
		: { type: 'multi', indices };
}

/**
 * Step 3 — Map Employees (WS-10). Aggregates the file's rows per employee, fuzzy-
 * matches each name against the company roster (with saved aliases short-circuiting),
 * and lets the user override, add-as-new, or reactivate inactive matches. On advance
 * to Step 4 it persists the resolved matches as `employee_name` aliases.
 */
export default function Step3MapEmployees({
	state,
	dispatch,
	companyId,
	payPeriodId,
	onBack,
	onCancel,
}: Step3MapEmployeesProps) {
	const [roster, setRoster] = useState<RosterEmployee[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false); // alias POST in flight
	// Only one Maps-to dropdown is open at a time; tracking it lets the pinned
	// "Add as new employee" header close the menu after it fires.
	const [openRowId, setOpenRowId] = useState<string | null>(null);
	// Inline name-edit for add-as-new rows: which row's input is open + its draft.
	// Transient UI only — the committed name lives in the reducer (addAsNewName).
	const [editingRowId, setEditingRowId] = useState<string | null>(null);
	const [draftName, setDraftName] = useState('');

	// Load roster + saved aliases, then compute the initial matches once.
	useEffect(() => {
		if (!companyId) {
			setLoading(false);
			setLoadError('Missing company context.');
			return;
		}
		let cancelled = false;
		const load = async () => {
			setLoading(true);
			setLoadError(null);
			try {
				const [rosterRes, aliasRes] = await Promise.all([
					postApi(
						EMPLOYEES_URL,
						{ companyId, status: 'all' },
						false,
						{ limit: 10000, page: 1 }
					),
					getApi(ALIASES_URL, { companyId, mappingType: 'employee_name' }),
				]);
				if (cancelled) return;

				// DefaultResponse wrapper + service result both nest under `data`:
				// response.data.data.data is the employee array.
				const rosterResult: RosterEmployee[] =
					rosterRes.data?.data?.data ?? [];
				setRoster(rosterResult);

				// Only compute on first entry; Back→Next preserves user resolutions
				// (the reducer guards too, but skipping avoids needless work).
				if (state.employeeRows.length === 0) {
					const nameColConfig = buildNameColConfig(state);
					if (!nameColConfig) {
						setLoadError('No employee-name column was mapped in Step 2.');
						setLoading(false);
						return;
					}

					const aliasMap = new Map<string, string>();
					for (const a of aliasRes.data?.aliases ?? []) {
						if (a.csvValue && a.employeeId) {
							aliasMap.set(a.csvValue, a.employeeId);
						}
					}

					const headers = (state.rawMatrix[state.headerRowIndex] ?? []).map(
						String
					);
					const dataRows = state.rawMatrix
						.slice(state.headerRowIndex + 1)
						.filter(
							(r) =>
								r.some((c) => String(c).trim() !== '') && !isTotalRow(r)
						);

					const matrix = aggregateByEmployee(headers, dataRows, nameColConfig);
					const costMappings = state.columnMappings.filter(
						(m) =>
							m.category !== 'employee_name' &&
							m.category !== 'skip' &&
							m.fieldRef.type !== 'none'
					);
					// P2.2: state.pdfExtraction.rowConfidence is aligned with rawMatrix's
					// data rows 1:1 by the backend (mergeToDetectedStructure.ts) — but only
					// trust that alignment when the lengths actually match (defensive:
					// skip confidence badges rather than risk mislabeling a row) since
					// dataRows here is independently re-derived from rawMatrix above.
					const rowConfidence = state.pdfExtraction?.rowConfidence;
					const perEmployeeConfidence =
						rowConfidence && rowConfidence.length === dataRows.length
							? confidenceByEmployee(dataRows, nameColConfig, rowConfidence)
							: undefined;
					const aggregatedRows = buildAggregatedRows(
						matrix,
						nameColConfig,
						costMappings,
						perEmployeeConfidence
					);
					const employeeRows = computeEmployeeRows(
						aggregatedRows,
						rosterResult,
						aliasMap
					);
					dispatch({
						type: 'STEP3_INITIALIZED',
						payload: { aggregatedRows, employeeRows },
					});
				}
				setLoading(false);
			} catch {
				if (!cancelled) {
					setLoadError('Could not load employees. Please try again.');
					setLoading(false);
				}
			}
		};
		void load();
		return () => {
			cancelled = true;
		};
		// One-shot per company; the reducer guards against clobbering resolutions.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId]);

	// Reconciliation aid — PDF-sourced imports only (no benefit-type signal for
	// CSV/XLSX). Fires once employee matches are resolved (needs the field
	// mapping from Step 2 AND the resolved employee id from here), guarded on
	// priorPeriodValues still being null so it doesn't refire on every later
	// override (see wizardReducer.ts's PRIOR_PERIOD_VALUES_LOADED/step3ResetFor).
	useEffect(() => {
		if (!state.pdfExtraction || !payPeriodId || state.priorPeriodValues !== null) return;
		const employeeIds = Array.from(
			new Set(
				state.employeeRows
					.map((r) => r.matchedEmployeeId)
					.filter((id): id is string => !!id)
			)
		);
		if (employeeIds.length === 0) return;
		const coveredBenefitTypes = Array.from(
			new Set(
				state.pdfExtraction.documents
					.map((d) => d.benefitType)
					.filter((t): t is Exclude<typeof t, null> => !!t)
			)
		);
		let cancelled = false;
		(async () => {
			try {
				const res = await postApi(PRIOR_PERIOD_VALUES_URL, {
					companyId,
					payPeriodId,
					employeeIds,
					coveredBenefitTypes,
				});
				if (cancelled) return;
				const rows: Array<{ employeeId: string; fieldId: string; priorValue: number; shouldClear: boolean; fieldName: string }> =
					res.data?.fields ?? [];
				const values: NonNullable<WizardState['priorPeriodValues']> = {};
				for (const row of rows) {
					for (const r of state.employeeRows) {
						if (r.matchedEmployeeId !== row.employeeId) continue;
						values[`${r.rowId}|${row.fieldId}`] = {
							priorValue: row.priorValue,
							shouldClear: row.shouldClear,
							fieldName: row.fieldName,
						};
					}
				}
				dispatch({ type: 'PRIOR_PERIOD_VALUES_LOADED', values });
			} catch {
				// Reconciliation is an aid, not a blocker — fail silently rather than
				// surfacing a loadError that would block Step 3 entirely.
			}
		})();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.pdfExtraction, state.employeeRows, state.priorPeriodValues, payPeriodId, companyId]);

	const rosterById = useMemo(() => {
		const m = new Map<string, RosterEmployee>();
		for (const e of roster) m.set(e.id, e);
		return m;
	}, [roster]);

	const rosterOptions = useMemo(() => {
		const active = roster
			.filter((e) => e.isActiveInApp)
			.map((e) => ({ value: e.id, label: e.fullName }));
		const inactive = roster
			.filter((e) => !e.isActiveInApp)
			.map((e) => ({ value: e.id, label: `${e.fullName} (inactive)` }));
		// "Add as new employee" is no longer a scrollable option — it's pinned as a
		// sticky header via dropdownRender (below) so it stays visible regardless of
		// scroll position, letting any row be (re)set to add-as-new.
		return [...active, ...inactive];
	}, [roster]);

	// ENG-414: the band counts describe what will actually be imported, so skipped
	// rows drop out of them and are reported separately as `skippedCount`.
	// `totalCount` stays the full row count — it reads "found in the file", and the
	// table below still lists every row, skipped ones included.
	const matchedCount = state.employeeRows.filter(
		(r) =>
			!r.skipped &&
			(r.matchStatus === 'matched' || r.matchStatus === 'saved')
	).length;
	const reviewCount = state.employeeRows.filter(
		(r) => !r.skipped && r.matchStatus === 'review'
	).length;
	const newCount = state.employeeRows.filter(
		(r) => !r.skipped && r.matchStatus === 'new'
	).length;
	// ENG-369: rows whose source name was blank. Unresolved errors that block advance
	// (like review rows) until mapped, renamed via add-as-new, or skipped.
	const blankCount = state.employeeRows.filter(
		(r) => !r.skipped && r.matchStatus === 'blank_name'
	).length;
	const skippedCount = state.employeeRows.filter((r) => r.skipped).length;
	const totalCount = state.employeeRows.length;
	// P2.2: like the other counts, skipped rows drop out — a row that won't be
	// imported doesn't need its extraction confidence flagged for review.
	const lowConfidenceCount = state.employeeRows.filter(
		(r) => !r.skipped && r.confidence === 'low'
	).length;
	const uncoveredPriorFieldCount = state.priorPeriodValues
		? state.employeeRows.filter(
				(r) =>
					!r.skipped &&
					Object.entries(state.priorPeriodValues!).some(
						([key, entry]) => key.startsWith(`${r.rowId}|`) && entry.shouldClear
					)
		  ).length
		: 0;

	// ENG-369: a 'new' row whose effective name is blank must not advance either.
	// This closes the one path a blank-name row can leave the 'blank_name' status
	// with an empty name: clicking "Add as new employee" (which seeds addAsNewName
	// from the blank csvName) and then cancelling the inline edit without typing one.
	// An empty-named new employee is never valid, so blocking it has no false positives.
	const isBlankNewName = (r: EmployeeRow): boolean =>
		r.matchStatus === 'new' && (r.addAsNewName ?? r.csvName).trim() === '';

	// Review-band rows must be confirmed or overridden before advancing (§5.2);
	// ENG-369 blank-name rows are unresolved errors that block the same way. A skipped
	// row isn't imported, so its unresolved state can't block Preview — skipping is
	// itself a resolution (ENG-414). But skipping *everything* leaves nothing to
	// import, so require at least one row that will actually be sent; otherwise Step 4
	// offers to confirm an empty import ("Adding 0 rows").
	const canAdvance =
		!saving &&
		state.employeeRows.some((r) => !r.skipped) &&
		state.employeeRows.every(
			(r) =>
				r.skipped ||
				(r.matchStatus !== 'review' &&
					r.matchStatus !== 'blank_name' &&
					!isBlankNewName(r))
		);

	// Why Preview is disabled, spelled out — same pattern as Step 2's "N column(s)
	// have a category selected but no field mapped" message, including click-to-
	// scroll to the first blocking row. Uses exactly canAdvance's row predicates,
	// so the message can't disagree with the button.
	const blockingRows = state.employeeRows.filter(
		(r) =>
			!r.skipped &&
			(r.matchStatus === 'review' || r.matchStatus === 'blank_name' || isBlankNewName(r))
	);
	const blankNewNameCount = blockingRows.filter(isBlankNewName).length;
	const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
	let validationError: string | null = null;
	if (!loading && state.employeeRows.length > 0) {
		if (blockingRows.length > 0) {
			const reasons = [
				blankCount > 0 &&
					`${plural(blankCount, 'has a blank name', 'have a blank name')} (pick an employee, add as new, or skip)`,
				reviewCount > 0 &&
					`${plural(reviewCount, 'fuzzy match needs', 'fuzzy matches need')} confirming`,
				blankNewNameCount > 0 &&
					`${plural(blankNewNameCount, 'new employee needs', 'new employees need')} a name`,
			].filter(Boolean);
			validationError = `${plural(blockingRows.length, 'employee needs', 'employees need')} attention before Preview: ${reasons.join('; ')}.`;
		} else if (!state.employeeRows.some((r) => !r.skipped)) {
			validationError = 'Every employee is skipped — keep at least one to import.';
		}
	}

	const [highlightedRows, setHighlightedRows] = useState<Set<string>>(new Set());
	const scrollToBlockingRows = () => {
		if (blockingRows.length === 0) return;
		document
			.getElementById(`step3-row-${blockingRows[0].rowId}`)
			?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		setHighlightedRows(new Set(blockingRows.map((r) => r.rowId)));
		window.setTimeout(() => setHighlightedRows(new Set()), 1600);
	};

	const isInactive = (row: EmployeeRow): boolean =>
		row.matchedEmployeeId != null &&
		rosterById.get(row.matchedEmployeeId)?.isActiveInApp === false;

	// "Reactivate" overlaps the other bands rather than being a fourth mutually-
	// exclusive status: a matched/saved row mapped to an inactive employee will be
	// reactivated on import (§5.1/§5.5). Counted from the same predicate that drives
	// the per-row "Will be reactivated…" note, so it stays in sync as the user remaps.
	// ENG-414: a skipped row is never imported, so it never reactivates anyone.
	const reactivateCount = state.employeeRows.filter(
		(r) => !r.skipped && isInactive(r)
	).length;

	// New-band rows have no matchedEmployeeId; the Select shows a resolved
	// "Add as new employee" placeholder rather than a concrete option value.
	// ENG-414: skipped rows read the same way — there is no "Skip" entry in
	// `options` to bind to, so blanking the value lets the placeholder act as the
	// display label (the add-as-new precedent). The row's real matchedEmployeeId
	// stays intact in state underneath, which is what makes un-skip restorable.
	const selectValue = (row: EmployeeRow): string | undefined =>
		row.skipped ? undefined : row.matchedEmployeeId ?? undefined;

	const selectPlaceholder = (row: EmployeeRow): string => {
		if (row.skipped) return 'Skip — will not be imported';
		return row.matchStatus === 'new'
			? 'Add as new employee'
			: 'Select employee…';
	};

	// Open the inline name-edit for an add-as-new row, prefilled with the current
	// add-as-new name (or the source name if none committed yet).
	const openEdit = (row: EmployeeRow) => {
		setEditingRowId(row.rowId);
		setDraftName(row.addAsNewName ?? row.csvName);
	};

	const confirmEdit = (rowId: string) => {
		const trimmed = draftName.trim();
		if (!trimmed) return; // a non-empty name is required
		dispatch({
			type: 'EMPLOYEE_ADD_AS_NEW_NAME_SET',
			payload: { rowId, addAsNewName: trimmed },
		});
		setEditingRowId(null);
	};

	const cancelEdit = () => setEditingRowId(null);

	// ENG-414: Skip / un-skip. Fires from the pinned dropdown entry only — like
	// "Add as new employee" it bypasses the Select's onChange/onSelect entirely, so
	// it can't trip the review-row double-dispatch the two callbacks guard against.
	const handleSkipToggle = (row: EmployeeRow) => {
		const skipped = !row.skipped;
		dispatch({
			type: 'EMPLOYEE_SKIP_SET',
			payload: { rowId: row.rowId, skipped },
		});
		// A skipped row shows no add-as-new editor; close it if this row's was open.
		if (skipped && editingRowId === row.rowId) setEditingRowId(null);

		// One-time advisory, on the first Skip of the session only (never on un-skip).
		if (skipped && !state.skipToastShown) {
			dispatch({ type: 'SKIP_TOAST_SHOWN' });
			showNotification({
				type: 'info',
				message: SKIP_NOTICE_TITLE,
				description: SKIP_NOTICE_DESCRIPTION,
				duration: SKIP_NOTICE_DURATION,
			});
		}
	};

	const handleOverrideChange = (rowId: string, value: string) => {
		// The pinned sentinel reverts the row to the add-as-new state (status 'new',
		// matchedEmployeeId null, addAsNewName = csvName), regardless of prior status,
		// and opens the inline name-edit prefilled with the source name. The dispatch
		// hasn't flushed yet, so prefill the draft directly from csvName.
		if (value === ADD_AS_NEW_VALUE) {
			const row = state.employeeRows.find((r) => r.rowId === rowId);
			dispatch({ type: 'EMPLOYEE_SET_ADD_AS_NEW', payload: { rowId } });
			setEditingRowId(rowId);
			setDraftName(row?.csvName ?? '');
			return;
		}
		// A manual override is always a deliberate, confident choice → 'matched'.
		// Picking an existing employee for a new-band row clears its add-as-new
		// default (the reducer nulls addAsNewName); close any open edit on this row.
		if (editingRowId === rowId) setEditingRowId(null);
		dispatch({
			type: 'EMPLOYEE_MATCH_OVERRIDDEN',
			payload: {
				rowId,
				matchedEmployeeId: value,
				// ENG-410: carry the chosen employee's display name for Step 4.
				matchedEmployeeName: rosterById.get(value)?.fullName ?? null,
				matchStatus: 'matched',
				inactive: rosterById.get(value)?.isActiveInApp === false,
			},
		});
	};

	const handlePreview = async () => {
		setSaving(true);
		try {
			// Only rows resolved to a real employee carry an alias now; 'new' rows
			// get theirs server-side on Confirm Import (§6.4). 'saved' rows re-upsert
			// idempotently on (companyId, mappingType, csvValue).
			// ENG-414: skipped rows are excluded — persisting an alias would teach the
			// roster that a non-employee line ("Employee Totals") maps to a real person,
			// and that alias would then short-circuit matching on every future import.
			const aliases: Array<{ csvValue: string; employeeId: string }> = [];
			for (const r of state.employeeRows) {
				if (
					!r.skipped &&
					(r.matchStatus === 'matched' || r.matchStatus === 'saved') &&
					r.matchedEmployeeId
				) {
					aliases.push({ csvValue: r.csvName, employeeId: r.matchedEmployeeId });
				}
			}

			await postApi(ALIASES_URL, {
				companyId,
				mappingType: 'employee_name',
				// WS-14 (ENG-333): Step 3 mappings are import-provenance.
				source: 'import',
				aliases,
			});
			dispatch({ type: 'ADVANCE_STEP' });
			setSaving(false);
		} catch {
			toastText('Failed to save employee mappings. Please try again.', 'error');
			setSaving(false);
		}
	};

	// P2.2: a secondary badge, independent of the match-status Tag above — a row
	// can be both e.g. "Matched" (name resolution) AND low-confidence (the AI
	// extraction itself, orthogonal to whether the name matched a roster entry).
	// Gold to read as "double-check the source amounts", distinct from every
	// statusTag color already in use (green/blue/orange/red/gray).
	const confidenceTag = (row: EmployeeRow) =>
		row.confidence === 'low' ? (
			<Tag color="gold" title="At least one source row for this employee was extracted with low AI confidence — verify amounts against the original document.">
				Low confidence
			</Tag>
		) : null;

	// Reconciliation aid — a fringe field this employee had a nonzero value for
	// last period, but no uploaded invoice this cycle appears to cover (see
	// priorPeriodReconciliationService.ts's shouldClear). Orthogonal to both
	// statusTag and confidenceTag — a row can carry all three independently.
	const priorPeriodTag = (row: EmployeeRow) => {
		if (!state.priorPeriodValues) return null;
		const prefix = `${row.rowId}|`;
		const hasUncovered = Object.entries(state.priorPeriodValues).some(
			([key, entry]) => key.startsWith(prefix) && entry.shouldClear
		);
		return hasUncovered ? (
			<Tag color="red" title="This employee had a value for a fringe benefit field last period that no invoice uploaded this cycle appears to cover — review before confirming.">
				No invoice this cycle
			</Tag>
		) : null;
	};

	const statusTag = (row: EmployeeRow) => {
		// ENG-414: Skip overrides the band badge — a skipped row's underlying status
		// is preserved in state for un-skip, but showing "Review"/"New" next to a
		// "Skip" dropdown would contradict itself.
		if (row.skipped) return <Tag>Skipped</Tag>;
		switch (row.matchStatus) {
			case 'matched':
				return <Tag color="green">Matched</Tag>;
			case 'saved':
				// Neutral — keeps Saved distinct from Matched (green) and Review (blue).
				return <Tag>Saved</Tag>;
			case 'review':
				// Blue — needs confirmation. Clearly distinct from New (orange); the old
				// gold read too close to orange at a glance (UAT feedback).
				return <Tag color="blue">Review · {row.matchScore}</Tag>;
			case 'new':
				return <Tag color="orange">New</Tag>;
			case 'blank_name':
				// ENG-369: red — an unresolved error, distinct from the actionable
				// orange (New) and blue (Review) bands.
				return <Tag color="red">Blank name</Tag>;
			default:
				return null;
		}
	};

	const columns: ColumnsType<EmployeeRow> = [
		{
			title: 'Employee (file)',
			key: 'csvName',
			width: '35%',
			// ENG-369: a blank-name row has no csvName to show — render a muted
			// placeholder instead of an empty cell so the row is still legible.
			render: (_: unknown, row) =>
				row.csvName.trim() === '' ? (
					<span className={styles['step3__blank-name']}>(blank name)</span>
				) : (
					<strong>{row.csvName}</strong>
				),
		},
		{
			title: 'Status',
			key: 'status',
			width: '20%',
			render: (_: unknown, row) => (
				<Space size={4} wrap>
					{statusTag(row)}
					{confidenceTag(row)}
					{priorPeriodTag(row)}
				</Space>
			),
		},
		{
			title: 'Maps to',
			key: 'mapsTo',
			render: (_: unknown, row) => (
				<div className={styles['step3__map-cell']}>
					<Select
						className={
							row.skipped
								? styles['step3__select--skipped']
								: row.matchStatus === 'new'
								? styles['step3__select--add-new']
								: undefined
						}
						style={{ width: '100%' }}
						value={selectValue(row)}
						showSearch
						// New-band and skipped rows read as resolved: the placeholder doubles
						// as a display label ("Add as new employee" / "Skip — will not be
						// imported") styled like real text.
						placeholder={selectPlaceholder(row)}
						filterOption={(input, option) =>
							String(option?.label ?? '')
								.toLowerCase()
								.includes(input.toLowerCase())
						}
						options={rosterOptions}
						open={openRowId === row.rowId}
						onOpenChange={(open) =>
							setOpenRowId(open ? row.rowId : null)
						}
						// "Add as new employee" and "Skip" are pinned as sticky headers above
						// the scrollable roster so they stay visible no matter how far the
						// user scrolls (UAT: add-as-new was the first option but scrolled
						// away). Both bypass the Select's onChange/onSelect — see
						// handleSkipToggle.
						dropdownRender={(menu) => (
							<>
								<div
									className={styles['step3__add-new-pinned']}
									// Keep the search input focused; let onClick run the action.
									onMouseDown={(e) => e.preventDefault()}
									onClick={() => {
										handleOverrideChange(row.rowId, ADD_AS_NEW_VALUE);
										setOpenRowId(null);
									}}
								>
									Add as new employee
								</div>
								<div
									className={styles['step3__skip-pinned']}
									onMouseDown={(e) => e.preventDefault()}
									onClick={() => {
										handleSkipToggle(row);
										setOpenRowId(null);
									}}
								>
									{row.skipped ? 'Undo skip' : 'Skip — do not import this row'}
								</div>
								{menu}
							</>
						)}
						// onSelect fires on any explicit pick — including re-selecting the
						// already-shown match, which onChange swallows (no value change). For
						// review rows that re-select is exactly the "confirm" gesture, so
						// onSelect owns their update; handleOverrideChange already flips the
						// row to 'matched' via EMPLOYEE_MATCH_OVERRIDDEN (ENG-371).
						onSelect={
							row.matchStatus === 'review'
								? (value: string) => {
										if (value !== ADD_AS_NEW_VALUE) {
											handleOverrideChange(row.rowId, value);
										}
								  }
								: undefined
						}
						// Review rows are owned by onSelect above; skipping them here avoids
						// a double EMPLOYEE_MATCH_OVERRIDDEN when a different employee is
						// picked (both onChange and onSelect would otherwise fire).
						onChange={(value: string) => {
							if (row.matchStatus === 'review') return;
							handleOverrideChange(row.rowId, value);
						}}
					/>

					{/* Review rows have a pre-populated fuzzy match but no auto-confirm.
					    Always-visible cue (not gated on the dropdown being open) telling the
					    user the match is provisional and how to commit it (ENG-371). Reuses
					    the muted reactivation-note styling. */}
					{/* ENG-414: all three per-row notes below are suppressed while the row
					    is skipped — it isn't being imported, so "confirm this match",
					    "new employee: …" and "will be reactivated" are all moot. */}
					{row.matchStatus === 'review' && !row.skipped && (
						<div className={styles['step3__inactive']}>
							<span className={styles['step3__inactive-note']}>
								Click the highlighted name to confirm, or choose a
								different employee.
							</span>
						</div>
					)}

					{/* ENG-369: blank source name — an unresolved error that blocks
					    Preview. The user must pick an existing employee, add-as-new with
					    a typed name, or Skip the row. Suppressed while skipped (the skip
					    is the resolution). */}
					{row.matchStatus === 'blank_name' && !row.skipped && (
						<div className={styles['step3__inactive']}>
							<span className={styles['step3__error-note']}>
								Employee name is blank — skip this row or fix the source
								file.
							</span>
						</div>
					)}

					{/* Inline name-edit. The input is gated on editingRowId (set only by
					    the add-as-new flows), so it shows immediately on "Add as new
					    employee" even before the reducer flips the row to 'new'. The
					    confirmed note shows for resolved new-band rows. */}
					{editingRowId === row.rowId ? (
						<div className={styles['step3__inline-input']}>
							<Input
								autoFocus
								size="small"
								value={draftName}
								placeholder="New employee name"
								onChange={(e) => setDraftName(e.target.value)}
								onPressEnter={() => confirmEdit(row.rowId)}
							/>
							<Button
								size="small"
								type="primary"
								aria-label="Confirm new employee name"
								onClick={() => confirmEdit(row.rowId)}
							>
								✓
							</Button>
							<Button
								size="small"
								aria-label="Cancel name edit"
								onClick={cancelEdit}
							>
								✗
							</Button>
						</div>
					) : (
						row.matchStatus === 'new' &&
						!row.skipped && (
							<div className={styles['step3__add-new-note']}>
								New employee: <strong>{row.addAsNewName ?? row.csvName}</strong>
								{' · '}
								<Button
									type="link"
									size="small"
									style={{ padding: 0 }}
									onClick={() => openEdit(row)}
								>
									Edit
								</Button>
							</div>
						)
					)}

					{isInactive(row) && !row.skipped && (
						<div className={styles['step3__inactive']}>
							<span className={styles['step3__inactive-note']}>
								Will be reactivated if imported with values
							</span>
						</div>
					)}
				</div>
			),
		},
	];

	if (loadError) {
		return (
			<div className={styles['step3']}>
				<Alert
					type="error"
					showIcon
					message="Could not load Step 3"
					description={loadError}
				/>
				<div className={styles['step3__footer']}>
					<Space>
						<Button className={styles['step3__btn-secondary']} onClick={onBack}>
							Previous
						</Button>
						<Button className={styles['step3__btn-secondary']} onClick={onCancel}>
							Cancel
						</Button>
					</Space>
				</div>
			</div>
		);
	}

	return (
		<div className={styles['step3']}>
			<div>
				<div className={styles['step3__summary']}>
					{totalCount} unique employees found:&nbsp;
					<span style={{ color: '#52c41a', fontWeight: 600 }}>
						{matchedCount} matched
					</span>
					{reviewCount > 0 && (
						<>
							{' · '}
							{/* Blue to match the Review badge (needs confirmation). */}
							<span style={{ color: '#1677ff', fontWeight: 600 }}>
								{reviewCount} review
							</span>
						</>
					)}
					{' · '}
					<span style={{ fontWeight: 600 }}>{newCount} new</span>
					{blankCount > 0 && (
						<>
							{' · '}
							{/* ENG-369: red — a blocking error the user must clear before
							    Preview; omitted entirely when zero. */}
							<span style={{ color: '#cf1322', fontWeight: 600 }}>
								{blankCount} blank
							</span>
						</>
					)}
					{reactivateCount > 0 && (
						<>
							{' · '}
							{/* Orange to read as an action item, matching the per-row
							    reactivation cue; omitted entirely when zero (like review). */}
							<span style={{ color: '#fa8c16', fontWeight: 600 }}>
								{reactivateCount} reactivate
							</span>
						</>
					)}
					{skippedCount > 0 && (
						<>
							{' · '}
							{/* ENG-414: muted grey — a skip is a resolved non-action, not
							    something to chase; omitted entirely when zero. */}
							<span style={{ color: '#8c8c8c', fontWeight: 600 }}>
								{skippedCount} skipped
							</span>
						</>
					)}
					{lowConfidenceCount > 0 && (
						<>
							{' · '}
							{/* P2.2: gold to match the Low confidence badge; omitted when zero
							    (never shown for CSV/XLSX imports, which have no confidence data). */}
							<span style={{ color: '#d4b106', fontWeight: 600 }}>
								{lowConfidenceCount} low confidence
							</span>
						</>
					)}
					{uncoveredPriorFieldCount > 0 && (
						<>
							{' · '}
							{/* Red to match the "No invoice this cycle" badge; omitted when
							    zero (only ever shown for a PDF-sourced import). */}
							<span style={{ color: '#cf1322', fontWeight: 600 }}>
								{uncoveredPriorFieldCount} no invoice this cycle
							</span>
						</>
					)}
				</div>
				<div className={styles['step3__summary-subtext']}>
					Aliases are saved when you click Preview. Fuzzy matches with a score of
					60–84 need your confirmation.
				</div>
			</div>

			<Spin spinning={loading}>
				<Table<EmployeeRow>
					className={styles['step3__table']}
					columns={columns}
					dataSource={state.employeeRows}
					rowKey="rowId"
					pagination={false}
					size="small"
					scroll={{ y: TABLE_SCROLL_Y }}
					onRow={(row) => ({ id: `step3-row-${row.rowId}` })}
					rowClassName={(row) =>
						highlightedRows.has(row.rowId) ? styles['step3__row-highlight'] : ''
					}
				/>
			</Spin>

			{validationError && (
				<div
					className={`${styles['step3__validation']} ${
						blockingRows.length > 0 ? styles['step3__validation--clickable'] : ''
					}`}
					{...(blockingRows.length > 0
						? {
								role: 'button',
								tabIndex: 0,
								title: 'Show the first employee that needs attention',
								onClick: scrollToBlockingRows,
								onKeyDown: (e: React.KeyboardEvent) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										scrollToBlockingRows();
									}
								},
						  }
						: {})}
				>
					<ExclamationCircleOutlined aria-hidden="true" />
					<span>{validationError}</span>
				</div>
			)}

			<div className={styles['step3__footer']}>
				<Space>
					<Button className={styles['step3__btn-secondary']} onClick={onBack}>
						Previous
					</Button>
					<Button className={styles['step3__btn-secondary']} onClick={onCancel}>
						Cancel
					</Button>
					<Button
						type="primary"
						disabled={!canAdvance}
						loading={saving}
						onClick={() => void handlePreview()}
						className="btn-black"
						style={{ height: '41px' }}
					>
						Preview
					</Button>
				</Space>
			</div>
		</div>
	);
}
